<template>
  <div
    :class="['video-card', { selected: isSelected }]"
    @mouseenter="handleCardMouseEnter"
    @mouseleave="handleCardMouseLeave"
    @click="handleCardClick"
  >
    <!-- Selection Indicator -->
    <div
      v-if="isSelected"
      class="video-card__selection"
    >
      <!-- Show order number for checkbox selection -->
      <div
        v-if="selectionType === 'checkbox' && selectionOrder !== null"
        class="order-number"
      >
        {{ selectionOrder }}
      </div>
      <!-- Show check icon for checkbox when no order number -->
      <feather-icon
        v-else-if="selectionType === 'checkbox'"
        icon="CheckIcon"
        size="16"
        class="check-icon"
      />
      <!-- Radio indicator -->
      <div
        v-else-if="selectionType === 'radio'"
        class="radio-indicator"
      />
    </div>

    <div class="video-card__media-container">
      <!-- Show video only when playing -->
      <video
        v-if="videoUrl && internalPlaying"
        :id="videoDomId"
        :src="videoUrl"
        class="video-card__video"
        muted
        loop
        preload="metadata"
        playsinline
        autoplay
        @loadedmetadata="onVideoReady"
        @ended="handleVideoEnded"
      >
        Your browser does not support the video tag.
      </video>

      <!-- Show thumbnail image by default -->
      <img
        v-else-if="thumbnailUrl"
        :src="thumbnailUrl"
        :alt="`Video thumbnail for ${videoId}`"
        class="video-card__thumbnail"
      >

      <!-- Fallback placeholder -->
      <div
        v-else
        class="video-card__placeholder"
      >
        <feather-icon
          icon="VideoIcon"
          size="36"
        />
      </div>

      <!-- Play button overlay on media (when not playing) -->
      <div
        v-if="videoUrl && !internalPlaying"
        class="video-card__play-overlay"
      >
        <button
          type="button"
          class="video-card__play-btn"
          :aria-label="'Play video'"
          @click.stop="togglePlay"
        >
          <feather-icon
            icon="PlayIcon"
            size="20"
          />
        </button>
      </div>

      <!-- Pause button overlay on video (when playing) -->
      <div
        v-if="videoUrl && internalPlaying"
        class="video-card__pause-overlay"
      >
        <button
          type="button"
          class="video-card__pause-btn"
          :aria-label="'Pause video'"
          @click.stop="togglePlay"
        >
          <feather-icon
            icon="PauseIcon"
            size="20"
          />
        </button>
      </div>

      <!-- Loading Spinner -->
      <div
        v-if="isLoading"
        class="video-card__spinner"
      >
        <div class="video-card__spinner-circle" />
      </div>

    </div>

    <!-- Video Title/Name (optional) -->
    <div
      v-if="showTitle && title"
      class="video-card__title"
    >
      {{ title }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'VideoCard',
  props: {
    // Video data
    videoId: {
      type: [String, Number],
      required: true,
    },
    videoUrl: {
      type: String,
      default: '',
    },
    thumbnailUrl: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },

    // Selection behavior
    selectionType: {
      type: String,
      default: 'checkbox', // 'checkbox' or 'radio'
      validator: value => ['checkbox', 'radio'].includes(value),
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    selectionOrder: {
      type: Number,
      default: null,
    },

    // Display options
    showTitle: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },

    // Note: isPlaying is managed internally, not by parent
    // Removed isPlaying prop - component manages its own playback state
  },
  data() {
    return {
      internalPlaying: false,
    }
  },
  computed: {
    videoDomId() {
      return `video-card-${String(this.videoId)}`
    },
  },
  methods: {
    handleCardMouseEnter() {
      // With thumbnail-first approach, no auto-play on hover
      // User must click play button to start video
    },

    handleCardMouseLeave() {
      // Let video play to completion naturally
      // No action needed on mouse leave
    },

    handleCardClick() {
      if (this.isLoading) return
      this.$emit('select', this.videoId)
    },

    togglePlay() {
      if (!this.videoUrl) return

      const newPlayingState = !this.internalPlaying
      this.internalPlaying = newPlayingState

      if (newPlayingState) {
        // For playing, wait for video element to be created
        this.$nextTick(() => {
          const el = this.getVideoElement()
          if (el && el.play) {
            const playPromise = el.play()
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(() => {})
            }
          }
        })
      } else {
        // For pausing, video element already exists
        const el = this.getVideoElement()
        if (el && el.pause) {
          el.pause()
        }
      }
    },

    handleVideoEnded() {
      this.internalPlaying = false
    },

    onVideoReady() {
      this.syncVideoElement()

      // If we're supposed to be playing, try to play now that video is ready
      if (this.internalPlaying) {
        const el = this.getVideoElement()
        if (el && el.play) {
          const playPromise = el.play()
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {})
          }
        }
      }
    },

    getVideoElement() {
      if (!this.videoId) return null
      try {
        return this.$el && this.$el.querySelector ? this.$el.querySelector(`#${this.videoDomId}`) : null
      } catch {
        return null
      }
    },

    syncVideoElement() {
      const el = this.getVideoElement()
      if (!el) return
      el.muted = true
      el.controls = false
    },
  },
}
</script>

<style lang="scss" scoped>
.video-card {
  position: relative;
  width: 100%;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border 0.3s ease;
  border: 2px solid transparent;
  display: flex;
  flex-direction: column;
  background: #f8f9ff;
  flex-shrink: 0;
  isolation: isolate;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &.selected {
    box-shadow: 0 20px 40px rgba(130, 90, 255, 0.45), 0 0 0 3px rgba(142, 197, 252, 0.3);
    outline: 3px solid #8ec5fc;
    border: 2px solid rgba(142, 197, 252, 0.6);
  }

  .video-card__selection {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%; // Changed to circle for radio button
    background: linear-gradient(135deg, #8ec5fc 0%, #a855f7 100%);
    border: 2px solid #8ec5fc;
    color: white;
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(130, 90, 255, 0.4);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    .check-icon {
      color: white;
    }

    .order-number {
      color: white;
      font-size: 12px;
      font-weight: 600;
      line-height: 1;
    }

    .radio-indicator {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: white;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    }
  }

  .video-card__media-container {
    position: relative;
    width: 100%;
    padding-bottom: 177.78%; // 9:16 aspect ratio (16/9 * 100 = 177.78%)
    overflow: hidden;
    background: #111827;
    flex-shrink: 0;
  }

  .video-card__video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .video-card__thumbnail {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .video-card__placeholder {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: #374151;
    color: #9ca3af;
  }

  .video-card__title {
    padding: 12px 10px;
    font-size: 13px;
    text-align: center;
    color: #fff;
    background: #000;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1.2;
    word-break: break-word;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    min-height: 44px;
    flex: 1;
  }

  .video-card__spinner {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(2px);
    background: rgba(17, 24, 39, 0.35);
    z-index: 3;
  }

  .video-card__spinner-circle {
    width: 48px;
    height: 48px;
    border: 4px solid rgba(255, 255, 255, 0.4);
    border-top-color: #e11c4e;
    border-radius: 50%;
    animation: video-card-spin 0.8s linear infinite;
  }

  .video-card__order-badge {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: linear-gradient(135deg, #e11c4e 0%, #f97316 100%);
    border: 2px solid #ffffff;
    border-radius: 50%;
    color: white;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    box-shadow: 0 2px 8px rgba(225, 28, 78, 0.4);
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.1);
      box-shadow: 0 4px 12px rgba(225, 28, 78, 0.6);
    }
  }
}

@keyframes video-card-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
